import { useTranslation } from 'react-i18n-lite'

import { useRef } from 'react'
import Icon from '../Icon'
import BoxInfoText from './BoxInfoText'
import { useNavigationSlider } from './hooks'
import {
  ArrowContainer,
  Arrows,
  ComponentStructureBox,
  ComponentStructureElementFrame,
  InterfaceElement
} from './styles'
import { NavigationSliderProps } from './type'

export enum NavigationSliderActionsIndexes {
  gotoStartPosition = 0,
  goToPreviousPosition = 1,
  goToNextPosition = 2,
  gotoEndPosition = 3
}

const NavigationSlider = ({
  currentPosition,
  first = 1,
  total,
  doubleArrowhead = false,
  arrowOrientationButtons = 'horizontalArrows',
  textForCurrent,
  textForTotal,
  hierarchyLevel = 'level_1',
  currentValueEditable = false,
  afterClick,
  className,
  heightClass = 'regular',
  fitContent = false,
  disabled = false,
  notFocusable = false,
  showTotalPages = true,
  hasPreviousPage,
  hasNextPage,
  backgroundColor,
  color,
  singleArrowIconName = 'NavigationRight'
}: NavigationSliderProps) => {
  const { t } = useTranslation()

  const buttonRefs = useRef<HTMLButtonElement[]>([])

  const assignButtonRef = (ref: HTMLButtonElement | null, index: number) => {
    if (ref) {
      buttonRefs.current[index] = ref
    }
  }

  const onArrowClick = (callback: VoidFunction, index: number) => {
    callback()
    onFocusButton(index, true)
  }

  const onFocusButton = (index: number, focusState: boolean) => {
    if (focusState) {
      buttonRefs.current[index].focus()
    } else {
      buttonRefs.current[index].blur()
    }
  }

  const isFirstPosition = currentPosition === first
  const isLastPosition = currentPosition === total
  const tabIndex = disabled || notFocusable ? -1 : 0

  const manualLeftArrowControl = typeof hasPreviousPage === 'boolean'
  const manualRightArrowControl = typeof hasNextPage === 'boolean'

  const showLeftArrow = manualLeftArrowControl
    ? hasPreviousPage
    : !isFirstPosition
  const showRightArrow = manualRightArrowControl ? hasNextPage : !isLastPosition

  const showDoubleLeftArrow = doubleArrowhead && !manualLeftArrowControl
  const showDoubleRightArrow = doubleArrowhead && !manualRightArrowControl

  const getSingleArrowIconName = () => {
    if (disabled) {
      return 'DisabledRightArrow'
    }

    if (notFocusable) {
      return 'NonFocusableArrowRight'
    }

    return singleArrowIconName
  }

  const getDoubleArrowIconName = () => {
    const doubleArrowIconName = 'NavigationDoubleRight'

    if (disabled) {
      return 'DisabledDoubleRightArrow'
    }

    if (notFocusable) {
      return 'NonFocusableDoubleArrowRight'
    }

    return doubleArrowIconName
  }

  const {
    goToNextPosition,
    goToPreviousPosition,
    gotoStartPosition,
    gotoEndPosition
  } = useNavigationSlider({
    first,
    total,
    afterClick
  })

  const rightArrowRotation =
    {
      horizontalArrows: 0,
      diagonalArrows: 45,
      verticalArrows: 90
    }[arrowOrientationButtons] || 0

  const leftArrowRotation = rightArrowRotation + 180

  if (!showTotalPages && !showLeftArrow && !showRightArrow) return null

  return (
    <InterfaceElement fitContent={fitContent} className={className}>
      <ComponentStructureBox
        hierarchyLevel={hierarchyLevel}
        heightClass={heightClass}
        disabled={disabled}
        backgroundColor={backgroundColor}
      >
        <ComponentStructureElementFrame
          heightClass={heightClass}
          disabled={disabled}
        >
          <Arrows heightClass={heightClass}>
            <ArrowContainer
              heightClass={heightClass}
              onClick={() =>
                onArrowClick(
                  gotoStartPosition,
                  NavigationSliderActionsIndexes.gotoStartPosition
                )
              }
              aria-label={t('navigation-slider.beginning')}
              visible={showDoubleLeftArrow && showLeftArrow}
              disabled={disabled}
              tabIndex={tabIndex}
              rotation={leftArrowRotation}
              color={color}
              notFocusable={notFocusable}
              ref={(el) =>
                assignButtonRef(
                  el,
                  NavigationSliderActionsIndexes.gotoStartPosition
                )
              }
              onMouseEnter={() =>
                onFocusButton(
                  NavigationSliderActionsIndexes.gotoStartPosition,
                  true
                )
              }
              onMouseLeave={() =>
                onFocusButton(
                  NavigationSliderActionsIndexes.gotoStartPosition,
                  false
                )
              }
            >
              <Icon iconName={getDoubleArrowIconName()} icSize='auto' />
            </ArrowContainer>
            <ArrowContainer
              heightClass={heightClass}
              onClick={() =>
                onArrowClick(
                  () => goToPreviousPosition(currentPosition),
                  NavigationSliderActionsIndexes.goToPreviousPosition
                )
              }
              aria-label={t('navigation-slider.previous')}
              visible={showLeftArrow}
              disabled={disabled}
              notFocusable={notFocusable}
              tabIndex={tabIndex}
              rotation={leftArrowRotation}
              color={color}
              ref={(el) =>
                assignButtonRef(
                  el,
                  NavigationSliderActionsIndexes.goToPreviousPosition
                )
              }
              onMouseEnter={() =>
                onFocusButton(
                  NavigationSliderActionsIndexes.goToPreviousPosition,
                  true
                )
              }
              onMouseLeave={() =>
                onFocusButton(
                  NavigationSliderActionsIndexes.goToPreviousPosition,
                  false
                )
              }
            >
              <Icon iconName={getSingleArrowIconName()} icSize='auto' />
            </ArrowContainer>
          </Arrows>
          {total && (
            <BoxInfoText
              first={first}
              current={currentPosition}
              total={total}
              textForCurrent={textForCurrent}
              textForTotal={textForTotal}
              currentValueEditable={currentValueEditable}
              onSubmitDatafield={afterClick}
              heightClass={heightClass}
              showTotalPages={showTotalPages}
              color={color}
            />
          )}
          <Arrows heightClass={heightClass}>
            <ArrowContainer
              heightClass={heightClass}
              onClick={() =>
                onArrowClick(
                  () => goToNextPosition(currentPosition),
                  NavigationSliderActionsIndexes.goToNextPosition
                )
              }
              aria-label={t('navigation-slider.next')}
              $secondary={true}
              visible={showRightArrow}
              disabled={disabled}
              tabIndex={tabIndex}
              rotation={rightArrowRotation}
              notFocusable={notFocusable}
              color={color}
              ref={(el) =>
                assignButtonRef(
                  el,
                  NavigationSliderActionsIndexes.goToNextPosition
                )
              }
              onMouseEnter={() =>
                onFocusButton(
                  NavigationSliderActionsIndexes.goToNextPosition,
                  true
                )
              }
              onMouseLeave={() =>
                onFocusButton(
                  NavigationSliderActionsIndexes.goToNextPosition,
                  false
                )
              }
            >
              <Icon iconName={getSingleArrowIconName()} icSize='auto' />
            </ArrowContainer>
            <ArrowContainer
              heightClass={heightClass}
              onClick={() =>
                onArrowClick(
                  gotoEndPosition,
                  NavigationSliderActionsIndexes.gotoEndPosition
                )
              }
              aria-label={t('navigation-slider.last')}
              visible={showDoubleRightArrow && showRightArrow}
              disabled={disabled}
              tabIndex={tabIndex}
              rotation={rightArrowRotation}
              notFocusable={notFocusable}
              color={color}
              ref={(el) =>
                assignButtonRef(
                  el,
                  NavigationSliderActionsIndexes.gotoEndPosition
                )
              }
              onMouseEnter={() =>
                onFocusButton(
                  NavigationSliderActionsIndexes.gotoEndPosition,
                  true
                )
              }
              onMouseLeave={() =>
                onFocusButton(
                  NavigationSliderActionsIndexes.gotoEndPosition,
                  false
                )
              }
            >
              <Icon iconName={getDoubleArrowIconName()} icSize='auto' />
            </ArrowContainer>
          </Arrows>
        </ComponentStructureElementFrame>
      </ComponentStructureBox>
    </InterfaceElement>
  )
}

export default NavigationSlider
